import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';

/**
 * Сторож «экран ещё на месте» для асинхронных колбэков.
 *
 * <p><b>Зачем.</b> Кладовщик уходит с вкладки, не дожидаясь ответа IndexedDB
 * или сервера, и колбэк, вернувшийся после этого, правит состояние
 * размонтированного компонента. React 18 на такое ничего не говорит —
 * предупреждение убрали в 18.0, — поэтому в браузере ошибка невидима вовсе,
 * а в тестах вылезает как {@code ReferenceError: window is not defined}
 * из среды, которую vitest уже снёс.
 *
 * <p><b>Почему это хук, а не три строки на месте.</b> Написанный руками,
 * он ровно один раз был написан неправильно — и этого хватило, чтобы
 * на сутки убить экран инвентаризации в разработке (см. ниже). Правильная
 * запись должна лежать в одном месте с объяснением, а не переписываться
 * заново на каждом экране.
 *
 * <p><b>Тело эффекта обязано возвращать сторож в {@code true}.</b> Это
 * не симметрия ради красоты: {@code StrictMode} в разработке прогоняет
 * эффекты дважды — setup → cleanup → setup, — и сторож, который только
 * снимается, после первого же прогона остаётся {@code false} на всю жизнь
 * компонента. Все прикрытые им колбэки замолкают: экран стоит на
 * «Загружаем…», кнопки, погашенные на время запроса, не зажигаются обратно,
 * а отказ сервера не показывается никому. То есть сторож от гонки
 * при размонтировании ломает экран сильнее, чем сама гонка.
 *
 * <p>Начальное значение — {@code true}: колбэк может уйти из обработчика
 * события раньше, чем React дойдёт до эффектов.
 *
 * @return ссылка, истинная ровно пока компонент смонтирован
 */
export function useMounted(): MutableRefObject<boolean> {
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return mounted;
}
